import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  tap,
  throwError,
} from 'rxjs';
import scopeLogger from '../../lib/scope-logger';
import Kibana from '#/utils/trackKibana.js';
import { createInteractiveEvent } from './create-interactive-event.js';
import { EventsList } from './events-list.js';
import interpolate from '#/lib/interpolate.js';
import DisplayMetric from '#/entities/metrics/display-metric.js';

export default EventsProvider;
/**
 * Keep events
 * @type {EventsList<string, InteractiveEvent>}
 */
const eventsList = new EventsList();

const console = scopeLogger('RMI: Events manager');
const kibana = new Kibana('events manager');

const selectEdgeMessages = filter(
  (message) => message.payload.type === 'promo',
);

const isCancelableState = (event) =>
  ['display', 'prepare'].includes(event.state);

/**
 *
 * @param {Object} DI
 * @param {HostGateway} DI.hostGateway
 * @param {ViewportMatcher} DI.viewportMatcher
 * @param {RenderersRegistry} DI.renderersRegistry
 * @constructor
 */
function EventsProvider({
  hostGateway,
  mns,
  creativeProvider,
  store,
  renderersRegistry,
  viewportMatcher,
  configProvider,
  metricCollector
}) {
  let pause = false;
  let suspended = false;
  let resumeTimeout;

  mns.message$.pipe(selectEdgeMessages).subscribe(handleEdgeMessage);

  hostGateway.message$
    .pipe(
      filter((message) => message.cmd === 'pause' || message.cmd === 'resume'),
      tap((message) => (pause = message.cmd === 'pause')),
    )
    .subscribe();

  hostGateway.message$
    .pipe(
      filter((message) => message.cmd === 'start'),
      /* this effect applied event on changing just virtual object */
      tap((message) => {
        cancelDisplays('user leave object');
        suspended = true;
        clearTimeout(resumeTimeout);

        const {
          payload: {
            config: {
              /*
               TODO: receive it from backend:
                - task for BE to provide this value for each video config
                - consume START message after receiving config
              */
              scheduler_suspend_timeout = getScheduleTimeoutFallback(message.payload),
            } = {}
          } = {},
        } = message;

        console.log('Suspend on change object, resume after ' + scheduler_suspend_timeout);
        resumeTimeout = setTimeout(() => {
          suspended = false;
        }, scheduler_suspend_timeout);
      }),
      /* next effect applied only when video_id changed */
      distinctUntilChanged(
        (prev, curr) => prev.payload.video_id === curr.payload.video_id,
      ),
      tap((message) => {
        const { video_id, channel_id } = message.payload;

        /* yet another toString conversion,
         TODO: Does it have a reason to move this into startListener? */
        const allowedIdList = [video_id, channel_id]
          .filter(Boolean)
          .map(String);
        const cleanPredicate = (event) =>
          false === allowedIdList.includes(event.videoId);

        eventsList.clear(cleanPredicate);
        // reset locked state for all renderers
        renderersRegistry.forEach((renderer) => (renderer.locked = false));

        // TODO: remove currentObjectIdentifier
        Kibana.setContext('currentObjectIdentifier', message.payload.video_id);
        Kibana.setContext('object.id', message.payload.video_id);
      }),
    )
    .subscribe();

  viewportMatcher.matches$
    .pipe(
      filter((isMatched) => !isMatched),
      tap(() => {
        cancelDisplays('viewport constraints');
      }),
    )
    .subscribe();

  hostGateway.position$
    .pipe(
      tap((message) => {
        // TODO: remove currentObjectPosition
        Kibana.setContext('currentObjectPosition', message.position);
        Kibana.setContext('object.position', message.position);
      }),
      filter(() => !(suspended || pause)),
      tap((message) => {
        onUpdatePosition(message, mns.message$.next.bind(mns.message$));
      }),
    )
    .subscribe();

  window.addEventListener('beforeunload', () => {
    metricCollector.flush();

    eventsList.forEach((event) => {
      if (['pending', 'prepare', 'await'].includes(event.state)) {
        kibana.warn('page closed before event shown', {
          eventState: event.state,
          interactive_id: event.interactive_id,
          binding: event.binding,
          video_id: event.videoId,
        });
      }
    });
  });

  /**
   * Canceling possible all displays
   * @param {string} reason
   */
  function cancelDisplays(reason) {
    eventsList.getList(isCancelableState).forEach((event) => {
      mns.message$.next(
        new CancelMessage(event, `display canceling, ${reason}`),
      );
    });
  }

  /**
   * @typedef {Object} EdgeMessage
   * @property {string} topic
   * @property {Object} payload
   * @property {'create'|'delete'|'targeted'} payload.cmd
   * @property {Object} payload.data
   * @property {Object} payload.data.params
   * @property {Object} payload.data.params.binding
   * @property {string} payload.data.params.binding.id
   * @property {string} payload.data.params.video_id
   * @property {string} payload.data.interactive_id
   */

  /**
   * @param {EdgeMessage} message
   */
  function handleEdgeMessage(message) {
    const { cmd, data } = message.payload;

    if (cmd === 'create') {
      const {
        params: { binding = {}, video_id },
        interactive_id,
      } = data;

      try {
        const interactiveEvent = createInteractiveEvent(
          message.topic,
          binding,
          interactive_id,
          video_id,
        );

        interactiveEvent.on('state:expired', (event, oldState) => {
          if (oldState === 'display') {
            mns.message$.next(
              new CancelMessage(event, `display canceling, expired`),
            );
          }
          event.state = 'deleted';
        });

        interactiveEvent.on('state:display', (event) => {
          metricCollector.push(new DisplayMetric(event));
        });

        if (eventsList.set(binding.id, interactiveEvent)) {
          kibana.info('create event', interactiveEvent);
        } else {
          console.warn('Event already exists, skip', interactiveEvent);
        }
      } catch (error) {
        console.error(error);
        kibana.error(error, {
          binding,
          interactive_id,
          video_id,
        });
      }
    }

    if (cmd === 'delete') {
      const event = eventsList.get(data.params.binding.id);
      if (event?.state === 'display') {
        mns.message$.next(
          new CancelMessage(event, `display canceling, event was deleted`),
        );
      }

      eventsList.delete(data.params.binding.id);
      kibana.debug('delete event', { binding: data.params.binding });
    }

    if (cmd === 'targeted') {
      const event = eventsList.get(data.params.binding.id);
      if (!event) {
        kibana.error('targeted event not found', {
          interactive_id: data.interactive_id,
          binding: data.params?.binding,
          video_id: data.params?.video_id,
        });
        return;
      }
      /*
       no targeting, e.g. no creative for this event
       just mark slot as used with state "shown"
      */
      if (!data.creative?.url) {
        event.state = 'shown';
        return;
      }

      event.target = data.creative;
      event.dispatch.hit();

      creativeProvider
        .getByUrl(data.creative.url)
        .pipe(catchError(onFetchFailure), map(toEvent))
        .subscribe({
          next(event) {
            event.state = 'await';
            /*
  // Disable this update, expect it will be called by position update,
   the worst lag is less than 1 sec. so it is acceptable.
   Otherwise, it should call update with the same argument, full position object, includes `duration`

            // immediately call update with latest know position
            onUpdatePosition({
                position: hostGateway.position$.value.position,
                offset: hostGateway.position$.value.start_time
              },
              mns.message$.next.bind(mns.message$)
            );
*/
          },
          error(error) {
            // the only one "expected" error is "no event",
            // but who knows what could happen
            kibana.error(error, {
              binding: data.params.binding,
              creative: data.creative,
            });
            console.error(error);
          },
          complete() {
            console.info('Fetching creative finished');
          },
        });

      function toEvent(creative) {
        const event = eventsList.get(data.params.binding.id);
        if (!event) {
          return throwError(() => {
            return new Error('no event to attach creative');
          });
        }

        event.creative = creative;

        return event;
      }

      function onFetchFailure(error) {
        const event = eventsList.get(data.params.binding.id);
        if (!event) {
          return throwError(() => {
            return new Error('no event to attach creative');
          });
        } else {
          const { status, message } = error;
          const newError = new Error(message || 'Creative fetch failure');
          newError.url = (error.url ?? data.creative.url).replace(/&rnd=[^&]+/, '');
          newError.status = status;
          if (Array.isArray(error)) {
            newError.reasons = error.map(({message, url, status}) => ({message, detail: {url, status}}));
          }
          event.dispatch.error(newError);
          return EMPTY;
        }
      }
    }
  }

  const channelPosition = (position, startOffset) => position + startOffset;
  const vodPosition = (position, _startOffset) => position;

  function onUpdatePosition(message, cb) {
    const { position, start_time: startOffset, duration } = message;

    /*
     * TODO: add getSlotDiff method in event
     *  - encapsulate making compare position to diff
     *
     */

    eventsList.forEach((event, _id) => {
      if (event.state === 'deleted') {
        // TODO: ensure it is safe, then real remove from list
        //  - unsafe case: event deleted, but still in use or have some pending process

        /* for the moment just do nothing with deleted events */
        return;
      }

      const [eventStart, eventEnd] = event.interval;

      /*
        VOD events **always** have ending before object actual end, e.g. duration
       */
      const getPosition = eventEnd <= duration ? vodPosition : channelPosition;
      const comparablePosition = getPosition(position, startOffset);

      let diff = Math.min(0, comparablePosition - eventStart);
      diff ||= Math.max(0, comparablePosition - eventEnd);

      const hasPositionInsideInterval = diff === 0;
      if (hasPositionInsideInterval) {
        switch (event.state) {
          case 'idle':
            try {
              kibana.debug('request targeting', {
                interactive_id: event.interactive_id,
                binding: event.binding,
              });
              /* Pretty inaccurate and ugly approach to construct actual topic name
               * TODO:
               *  - find a way to encapsulate using template;
               *  - also moving such template from per video config to global config may make it simplest
               **/
              const topic = interpolate(
                store.getState().app.contentRequestTopic,
                {
                  video_id: event.videoId,
                },
              );
              // TODO: Does it make sense to set timeout
              //  to report about "failed request",
              //  e.g. no targeting message in X seconds
              mns
                .publish(topic, {
                  promo_id: event.interactive_id,
                  binding: event.binding,
                })
                .then(() => {
                  event.state = 'pending';
                });
              event.state = 'content requested';
              event.displayBlockers.add('no target');
            } catch (error) {
              event.dispatch.error(error.toString());
            }
            break;
          case 'await': {
            event.displayBlockers.clear();
            const renderer = renderersRegistry.getRenderer(event.creative);

            if (!renderer) {
              event.displayBlockers.add('no renderer');
              return;
            }

            if (renderer.locked) {
              event.displayBlockers.add('renderer locked');
              return;
            }

            event.renderer = renderer;

            if (!viewportMatcher.isMatched) {
              event.displayBlockers.add('viewport constraints');
              return;
            }

            if (renderersRegistry.isSome((renderer) => renderer.locked)) {
              event.displayBlockers.add('other show in progress');
              return;
            }

            cb({
              topic: event.origin,
              payload: {
                cmd: 'display',
                type: renderer.name,
                data: {
                  creative: event.creative,
                  binding: event.binding,
                },
              },
            });
            event.displayBlockers.add('display request published');
            break;
          }
          default:
        }
        return;
      }

      switch (event.state) {
        case 'display':
          cb({
            topic: event.origin,
            payload: {
              cmd: 'close',
              type: event.renderer.name,
              data: {
                reason: 'out of bounds',
                diff,
              },
            },
          });
          break;
        case 'content requested':
        case 'pending':
        case 'prepare':
        case 'await':
          event.dispatch.leaveSlot(diff);
          break;
        case 'shown':
        case 'failed':
          event.dispatch.leaveSlot();
          break;
        default:
      }
    });
  }
}

function CancelMessage(event, reason) {
  return {
    topic: event.origin,
    payload: {
      cmd: 'cancel',
      type: event.renderer.name,
      data: {
        reason,
      },
    },
  };
}

function getScheduleTimeoutFallback({ content_type }){
  if (['vod', 'live'].includes(content_type)) {
    return 0;
  }
  return 10_000;
}
